import { CurrencyRupee, ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Checkbox, Typography } from "@mui/material";
import React, { useCallback, useState } from "react";
import { Button, Input } from "../../../../components";
import { getProjectsByNgo } from "../../../../services/Project.service";
import { pluralize } from "../../../../utils";
import cn from "../../../../utils/cn";
import { showError } from "../../../../utils/error";
import indianNumberFormatter from "../../../../utils/indian-number-formatter";
import styles from "../ngo-listing.module.css";
import { NGOCardProps } from "./props";

const NGOCard: React.FC<NGOCardProps> = React.memo(
  ({
    selectedNGOS,
    ngo,
    onCheckboxChange,
    onViewMoreClick,
    onValueChange,
    isVoting,
    errors,
    onProjectToggle,
  }) => {
    const {
      _id,
      name,
      title,
      type,
      image,
      totalVotes,
      totalDonations,
      totalGoal,
    } = ngo;
    const includedNGO = selectedNGOS.find((n) => n._id === _id);
    const isNGOSelected = !!includedNGO;
    const [projects, setProjects] = useState<any[]>([]);
    const [loadingProjects, setLoadingProjects] = useState(false);

    const [projectsOpen, setProjectsOpen] = useState(false);

    const handleToggleProjects = useCallback(async () => {
      if (projectsOpen) {
        setProjectsOpen(false);
        return;
      }

      setProjectsOpen(true);

      if (projects.length > 0) return;

      setLoadingProjects(true);
      try {
        const response = await getProjectsByNgo(_id, false);

        if (response?.success) {
          setProjects(response.data || []);
        } else {
          setProjects([]);
        }
      } catch (error: any) {
        showError(error);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    }, [_id, projects.length, projectsOpen]);

    return (
      <div className={cn(styles.card, includedNGO ? styles.selectedCard : "")}>
        {/* Existing NGO Card Content */}
        <div className={styles.ngoCard}>
          <div className={styles.ngoDetails}>
            <div className={styles.ngoDetailsLeft}>
              <Box className={styles.checkBoxContainer}>
                <Checkbox
                  sx={{ p: 0 }}
                  checked={!!includedNGO}
                  onChange={(e) => onCheckboxChange(e, _id)}
                />
                {isVoting && (
                  <p className={styles.voteText}>
                    {pluralize(totalVotes, "Vote")}
                  </p>
                )}
              </Box>
              <h2 className={styles.ngoName}>{name?.toUpperCase()}</h2>
              <p className={styles.ngoDescription}>{title}</p>
              {!isVoting && (
                <p className={styles.totalDonations}>
                  Total Donations :{" "}
                  <span>₹{indianNumberFormatter(String(totalDonations))}</span>
                </p>
              )}
              <Button
                variant="text"
                className={styles.viewMoreBtn}
                onClick={onViewMoreClick}
              >
                View More
              </Button>
            </div>
            <div>
              <img alt="ngo logo" src={image} className={styles.ngoImage} />
            </div>
          </div>
        </div>

        {/* Collapsible Projects Section inside each NGO card */}
        {!isVoting && (
          <Box sx={{ mt: 2, px: 2, pb: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                py: 1,
              }}
              onClick={handleToggleProjects}
            >
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="primary.main"
              >
                Our Impact Projects
              </Typography>
              {projectsOpen ? <ExpandLess /> : <ExpandMore />}
            </Box>

            {projectsOpen && (
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {loadingProjects ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    Loading projects...
                  </Typography>
                ) : projects.length > 0 ? (
                  projects.map((project, idx) => {
                    const checkboxId = `project-${_id}-${idx}`;

                    return (
                      <Box
                        key={project._id || idx}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                        }}
                      >
                        <Checkbox
                          id={checkboxId}
                          disabled={!isNGOSelected}
                          onChange={(e) =>
                            onProjectToggle?.(
                              _id,
                              project._id,
                              e.target.checked
                            )
                          }
                        />
                        <Box
                          component="label"
                          htmlFor={checkboxId}
                          sx={{
                            cursor: isNGOSelected ? "pointer" : "not-allowed",
                            opacity: isNGOSelected ? 1 : 0.6,
                            flex: 1,
                            pt: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                textAlign: "left",
                              }}
                            >
                              <Typography fontWeight={600} fontSize={14}>
                                {project.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                fontSize={13}
                                sx={{ mt: 0.5 }}
                              >
                                {project.cause}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                textAlign: "right",
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.primary"
                                fontSize={13}
                                sx={{ mt: 0.5 }}
                              >
                                Goal: ₹
                                {indianNumberFormatter(
                                  String(project?.goal || 0)
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.primary"
                                fontSize={13}
                                sx={{ mt: 0.5 }}
                              >
                                Donated: ₹
                                {indianNumberFormatter(
                                  String(project?.totalDonated || 0)
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 1 }}
                  >
                    No projects found
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Existing Donation Input Section */}
        <div className={styles.donateCard}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}
          >
            <Typography sx={{ fontSize: "11px", color: "#676767" }}>
              Goal: ₹{indianNumberFormatter(String(totalGoal))}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: "#676767" }}>
              Achieved: ₹{indianNumberFormatter(String(totalDonations))}
            </Typography>
          </Box>
          {includedNGO && (
            <Input
              fullWidth
              value={includedNGO.value ?? ""}
              label="Enter Amount"
              placeholder="Type the amount you want to pledge"
              slotProps={{
                input: {
                  startAdornment: <CurrencyRupee sx={{ fontSize: 18 }} />,
                },
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onValueChange(e, _id)
              }
              error={!!errors[_id]}
              helperText={errors[_id]}
            />
          )}
        </div>

        <span className={styles.sectorBadge}>{type?.name?.toUpperCase()}</span>
      </div>
    );
  }
);

export default NGOCard;
